import { createElement } from 'react'

import colorUtil from 'color'

import { Close } from '@mui/icons-material'
import { listItemButtonClasses } from '@mui/material/ListItemButton'
import { alpha, createTheme, darken } from '@mui/material/styles'

import appConfig from '~/src/App/config'
import createLogger from '~/src/Lib/Logging'
import { getMainColor } from '~/src/Lib/Utils/color'
import { EMPTY_OBJECT } from '~/src/Lib/Utils/constants'
import memoize, { shallowEqualsMemoizer } from '~/src/Lib/Utils/memoizer'

import { muiBreakpoints, spacing, SPACING } from './basics'
import { getInputColorStates, getStringableColorWithStates, palettes, states } from './colors'
import getGlobals from './main'
import { boldFontFamily, defaultFontFamily, pxToRem, typography as defaultTypography } from './typography'

export * from './basics'
export * from './colors'
export * from './main'
export { getGlobals }
export * from './typography'

const DEFAULT_BORDER_RADIUS = 4

const logger = createLogger('UI/Mui')

const transparent = 'transparent'
const { [appConfig.APP ?? 'AROYA']: defaultPalette } = palettes

const getPaletteColor = (palette, colorKey = 'primary') => {
  const color = palette[colorKey]
  return color?.main ? color : palette.primary
}

const getPaletteColorStates = (palette, colorKey = 'primary') => {
  const paletteColor = getPaletteColor(palette, colorKey)
  return {
    ...getInputColorStates(paletteColor.main),
    ...paletteColor,
  }
}

const getInputColorStyles = ({ colorKey = 'primary', error, hasValue, inputProps, value }, colors) => {
  const paletteColor = getPaletteColorStates(colors, colorKey)
  let color = colors.contrast.disabled
  let borderColor = color
  let backgroundColor = paletteColor.enabled

  if (error) {
    color = colors.error.main
    borderColor = color
    backgroundColor = colors.error.enabled
  } else if (hasValue || value || (inputProps && inputProps.value)) {
    color = colors.text.primary
    borderColor = paletteColor.main
  }

  return {
    backgroundColor,
    borderColor,
    color,
    '& .MuiInputBase-input': {
      color: colors.text.primary,
    },
    '& input::placeholder': {
      color: colors.contrast.disabled,
      opacity: 1,
    },
    '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor,
    },
    '&.MuiFilledInput-root': {
      backgroundColor,
      '&::before': {
        borderColor,
      },
      '&:hover': {
        backgroundColor: paletteColor.active,
      },
      '&.Mui-focused:not(:hover, :has(:autofill, :-webkit-autofill))': {
        backgroundColor: paletteColor.enabled,
      },
    },
  }
}

const severities = ['Error', 'Warning', 'Info', 'Success']
const variants = ['filled', 'outlined']
const getAlertVariantRules = palette =>
  severities.reduce((rules, severity) => {
    const selector = variants.map(variant => `&.MuiAlert-${variant}${severity}`).join(', ')
    rules[selector] = {
      color: palette[severity.toLowerCase()].main,
      borderColor: 'currentColor',
    }
    return rules
  }, {})
const addAlertVariantPlaceholders = existing =>
  severities.reduce((placeholders, severity) => {
    variants.forEach(variant => {
      const selector = `${variant}${severity}`
      if (selector in placeholders) return
      placeholders[selector] = {}
    })
    return placeholders
  }, existing)

export const buildTheme = memoize((originalPalette, options = EMPTY_OBJECT) => {
  const initialTheme = createTheme({ cssVariables: true, palette: { mode: 'dark' } })
  let palette = { ...initialTheme.palette, ...defaultPalette, ...originalPalette }
  const { primary, ...restPalette } = palette

  const main = palette?.primary?.main ?? palette.appPrimary.main
  const inputColors = getInputColorStates(main)

  palette = {
    ...Object.entries(restPalette).reduce((colors, [key, color]) => {
      colors[key] =
        typeof color === 'object' && 'main' in color ? initialTheme.palette.augmentColor({ color, name: key }) : color
      return colors
    }, {}),
    ...inputColors,
    primary: initialTheme.palette.augmentColor({
      color: { ...primary, ...inputColors },
      name: 'primary',
    }),
    states,
  }

  const { contrast } = palette
  palette.action = {
    active: contrast.inactive,
    hover: contrast.enabled,
    hoverOpacity: states.enabled,
    selected: contrast.enabled,
    selectedOpacity: states.enabled,
    disabled: contrast.disabled,
    disabledBackground: colorUtil(contrast.main).alpha(states.enabled / 4),
    disabledOpacity: states.disabled,
    focus: contrast.active,
    focusOpacity: states.active,
    activatedOpacity: states.active,
  }

  const typography = {
    boldFontFamily,
    fontFamily: defaultFontFamily,
    useNextVariants: true,
    ...Object.fromEntries(
      Object.entries(defaultTypography).map(([name, config]) => {
        if (name.match(/^(h\d|subtitle)/)) {
          return [name, { ...config, color: palette.text.primary }]
        }

        if (name.startsWith('body')) {
          return [name, { ...config, color: palette.text.secondary }]
        }

        return [name, config]
      }),
    ),
  }

  const { defaultBorderRadius: borderRadius = DEFAULT_BORDER_RADIUS } = options

  const theme = createTheme({
    alpha,
    breakpoints: muiBreakpoints,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '.Mui-disabled': {
            opacity: states.inactive,
          },
          '.aroya': {
            overflow: 'hidden',
          },
          '.aroya *': {
            boxSizing: 'border-box',
          },
          '.aroya #root': {
            minWidth: '100vw',
            maxWidth: '100vw',
          },
          '.aroya ::-webkit-scrollbar': {
            width: 6,
            height: 6,
            backgroundColor: `var(--scroll-bg, ${palette.primary.active})`,
            borderRadius: 3,
          },
          '.aroya ::-webkit-scrollbar-thumb': {
            backgroundColor: `var(--scroll-fg, ${palette.primary.disabled})`,
            borderRadius: 3,
            border: 'none !important',
          },
          '.aroya a:not([class])': {
            color: 'inherit',
          },
          '.aroya a:not(.aroya__link-underline)': {
            textDecoration: 'none',
          },
          '.aroya body': {
            color: palette.text.primary,
            fontSize: '0.875rem',
            fontWeight: 400,
            fontFamily: typography.fontFamily,
          },
          '.center-all': {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          },
          '@keyframes aroya-fade-in': {
            from: {
              opacity: 0,
            },
            to: {
              opacity: 1,
            },
          },
          '.fade-in': {
            opacity: 0,
            animationDelay: '800ms',
            animationDuration: '150ms',
            animationFillMode: 'forwards',
            animationIterationCount: 1,
            animationName: 'aroya-fade-in',
            animationTimingFunction: 'ease-in-out',
          },
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
            {
              transition: 'background-color 5000s ease-in-out 0s',
              caretColor: palette.background.dark,
              WebkitBoxShadow: '0 0 0 30px transparent inset !important',
              WebkitTextFillColor: `${palette.background.dark} !important`,
            },
        },
      },
      MuiAlert: {
        styleOverrides: addAlertVariantPlaceholders({
          root: {
            position: 'relative',
            alignItems: 'center',
            ...getAlertVariantRules(palette),
          },
          filledInfo: {
            backgroundColor: 'transparent',
            background: inputColors.active,
            '& .MuiAlert-message > div': {
              color: palette.appPrimary.main,
            },
            '& .MuiAlert-icon': {
              color: palette.appPrimary.main,
            },
          },
          icon: {
            alignItems: 'center',
            alignContent: 'center',
            '.MuiAlert-filledError &': {
              color: palette.text.primary,
            },
          },
          message: {
            padding: 0,
          },
        }),
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiInputLabel-shrink + .MuiFilledInput-root': {
              paddingTop: 0,
            },
            '& .MuiInputBase-colorSecondary .MuiAutocomplete-clearIndicator, & .MuiInputBase-colorSecondary .MuiAutocomplete-popupIndicator':
              {
                color: palette.secondary.main,
              },
          },
          clearIndicator: {
            color: main,
          },
          endAdornment: {
            marginRight: '1rem',
          },
          hasClearIcon: {},
          hasPopupIcon: {},
          popupIndicator: {
            color: main,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            padding: spacing(1.25, 2.5),
          },
          colorDefault: {
            backgroundColor: colorUtil(palette.secondary.light).alpha(states.inactive).string(),
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            transitionDelay: '250ms',
            backgroundColor: palette.contrastInverse.inactive,
            backdropFilter: 'blur(8px)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              opacity: states.disabled,
              filter: 'grayscale(0.68)',
            },
          },
          text: {
            padding: spacing(0.5, 2),
            borderRadius: '1rem',
          },
          textPrimary: {
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.primary.main,
            },
          },
          textSecondary: {
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.secondary.main,
            },
          },
          outlined: {
            backgroundColor: transparent,
            borderColor: 'currentColor',
            '& $label': {
              color: 'inherit',
            },
            '&:before': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              content: '""',
              backgroundColor: transparent,
              borderRadius,
              pointerEvents: 'none',
              opacity: 0,
            },
            '&:active:before': {
              opacity: states.active,
            },
            '&:focus:before': {
              opacity: states.focused,
            },
            '&:hover:before': {
              opacity: states.hovered,
            },
            '&$outlinedPrimary, &$outlinedSecondary': {
              backgroundColor: transparent,
              position: 'relative',
              borderColor: 'currentColor',
              '&:active, &:focus, &:hover': {
                position: 'relative',
                borderColor: 'currentColor',
              },
              '&:active:before, &:focus:before, &:hover:before': {
                backgroundColor: 'currentColor',
              },
            },
          },
          outlinedPrimary: {
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.primary.main,
            },
          },
          outlinedSecondary: {
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.secondary.main,
            },
          },
          contained: {
            '&:before': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              content: '""',
              backgroundColor: transparent,
              // borderRadius,
              pointerEvents: 'none',
            },
            '&:hover': {
              '&:before': {
                backgroundColor: palette.contrast.hovered,
              },
            },
            '&:active, &:focus': {
              '&:before': {
                backgroundColor: palette.contrast.active,
              },
            },
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.contrast.strong,
            },
          },
          containedPrimary: {
            position: 'relative',
            [['hover', 'active', 'focus']
              .map(state => `&:not(.${listItemButtonClasses.root}, .Mui-disabled):${state}`)
              .join(', ')]: {
              backgroundColor: main,
            },
          },
          containedSecondary: {
            backgroundColor: palette.secondary.main,
          },
          textSizeLarge: {
            padding: '11px 16px',
          },
          textSizeSmall: {
            padding: '7.5px 12px',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: undefined,
            '&.MuiCheckbox-colorPrimary': {
              color: main,
            },
          },
        },
      },
      MuiChip: {
        defaultProps: {
          deleteIcon: createElement(Close, { color: 'inherit' }),
        },
        styleOverrides: {
          root: {
            fontFamily: boldFontFamily,
            transition: 'none',
            '&.MuiChip-outlined': {
              borderWidth: 2,
            },
            '&.MuiChip-outlined.MuiChip-colorPrimary:active': {
              background: palette.primary.active,
            },
            '&.MuiChip-outlined.MuiChip-colorPrimary:hover': {
              background: palette.primary.enabled,
            },
          },
        },
      },
      MuiDateTimePickerToolbar: {
        styleOverrides: {
          timeDigitsContainer: {
            alignItems: 'center',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            '&.MuiTypography-root': {
              fontSize: 'unset',
            },
            ...Object.fromEntries(
              Object.entries(defaultTypography).map(([name, config]) => [
                `&.MuiTypography-root.MuiTypography-${name}`,
                { fontSize: config.fontSize },
              ]),
            ),
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              opacity: states.disabled,
              filter: 'grayscale(0.68)',
            },
          },
          primary: {
            position: 'relative',
            color: palette.primary.contrastText ?? palette.text.primary,
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              color: palette.text.primary,
            },
            '&:before': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              content: '""',
              backgroundColor: transparent,
              borderRadius: '50%',
              pointerEvents: 'none',
            },
            '&:hover': {
              backgroundColor: main,
              '&:before': {
                backgroundColor: palette.contrast.hovered,
              },
            },
            '&:active, &:focus': {
              backgroundColor: main,
              '&:before': {
                backgroundColor: palette.contrast.active,
              },
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            '&:not(.MuiInputBase-sizeSmall)': {
              minHeight: '3.5rem',
            },
            '&.MuiInputBase-sizeSmall': {
              minHeight: '2.5rem',
              '& .MuiFilledInput-input': {
                padding: spacing(1, 0, 0, 1.5),
              },
            },
            '&.MuiInputBase-root:has(input:-webkit-autofill)': {
              backgroundColor: 'rgb(232, 240, 254)',
            },
          },
          input: {
            '&.MuiInputBase-input:-webkit-autofill': {
              boxShadow: 'none',
              WebkitTextFillColor: `${palette.background.dark} !important`,
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'filled',
        },
        styleOverrides: {
          root: {
            '&:has(input:autofill, input:-webkit-autofill) .MuiFormLabel-root.MuiInputLabel-root': {
              color: primary.dark,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: spacing(2),
            '&.Mui-disabled': {
              opacity: 0.6,
            },
          },
        },
      },
      MuiFormLabel: {
        defaultProps: {
          color: 'primary',
        },
        styleOverrides: {
          root: ({ ownerState = EMPTY_OBJECT, theme: { palette: colors } }) => {
            const color = getPaletteColor(colors, ownerState.color).main

            return {
              '&:not(.Mui-error)': {
                color,
                '&.Mui-focused': {
                  color,
                },
              },
            }
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          colorSecondary: {
            color: palette.text.inactive,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const sharedStyles = {
              maxHeight: '3rem',
              '&.MuiIconButton-sizeSmall': {
                maxHeight: '1.875rem',
                maxWidth: '1.875rem',
              },
              '.MuiInputAdornment-root &': {
                color: 'currentColor',
              },
            }
            if (!ownerState.color || ownerState.color === 'default') {
              return {
                color: 'inherit',
                ...sharedStyles,
              }
            }
            return sharedStyles
          },
          sizeSmall: {},
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ ownerState = EMPTY_OBJECT, theme: { palette: colors } }) => ({
            color: colors.text.primary,
            ...getInputColorStyles(
              {
                ...ownerState,
                colorKey: ownerState.color,
              },
              colors,
            ),
            '.MuiInputLabel-shrink + & > .MuiInputBase-input': {
              top: spacing(),
            },
            '.MuiInputLabel-shrink + & > .MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)':
              {
                top: spacing(),
                marginTop: spacing(0.5),
              },
            '&.Mui-focused .MuiInputBase-input:focused': {
              caretColor: colors.contrast.disabled,
            },
            '&.MuiInput-root:has(input:autofill, input:-webkit-autofill)': {
              backgroundColor: 'rgb(232, 240, 254)',
              '& .MuiInputAdornment-root': {
                filter: 'brightness(0.8)',
                color: colors.background.dark,
              },
            },
            '& input:autofill, & input:-webkit-autofill': {
              boxShadow: 'none',
              color: colors.background.dark,
              caretColor: colors.background.dark,
              textFillColor: colors.background.dark,
              WebkitTextFillColor: `${colors.background.dark} !important`,
            },
          }),
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            position: 'relative',
            color: main,
            '.Mui-disabled &': {
              opacity: 0.32,
            },
            '.Mui-error &': {
              color: 'inherit',
            },
          },
          positionEnd: {
            paddingLeft: '0.75rem',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          formControl: {
            transform: 'translate(16px, 16px) scale(1)',
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(13px, 12px) scale(1)',
            },
          },
          shrink: {
            transform: 'translate(12px, 7px) scale(0.75)',
            '&.MuiInputLabel-outlined': {
              transform: 'translate(12px, -7px) scale(0.75)',
            },
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(12px, 2px) scale(0.75)',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 6,
            borderRadius: 3,
          },
          bar: {
            borderRadius: 3,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary?.active ?? defaultPalette.blue.active,
            },
            '&.MuiListItem-padding:not(.MuiListItem-dense, .MuiListItem-secondaryAction)': {
              padding: theme.spacing(1.5, 2),
            },
            '&.MuiListItem-dense': {
              padding: theme.spacing(0.75, 2),
            },
          }),
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: main,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            lineHeight: 'normal',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: {
            '&.MuiPopover-root[aria-hidden="true"]': {
              pointerEvents: 'none',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            minHeight: 32,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius,
          },
        },
      },
      MuiPickerDTTabs: {
        styleOverrides: {
          tabs: { backgroundColor: transparent },
        },
      },
      MuiPickersTextField: {
        defaultProps: {
          variant: 'filled',
        },
        styleOverrides: {
          root: ({ ownerState = EMPTY_OBJECT, theme: { palette: colors } }) => {
            const { active, enabled, main } = getPaletteColorStates(colors, ownerState.inputColor)
            const params = {
              colorKey: ownerState.inputColor,
              error: ownerState.hasFieldError,
              hasValue: !ownerState.isFieldValueEmpty,
            }
            const { backgroundColor, borderColor } = getInputColorStyles(params, colors)

            return {
              '& .MuiInputLabel-root:not(.Mui-error)': {
                color: main,
                '&.Mui-focused': {
                  color: main,
                },
              },
              '& .MuiInputAdornment-root, & [data-mui-picker-open-button="true"]': {
                color: main,
              },
              '& .MuiPickersInputBase-root.MuiPickersFilledInput-root:not(.Mui-disabled)': {
                backgroundColor,
              },
              '& .MuiPickersInputBase-root:not(.Mui-disabled):hover': {
                backgroundColor: active,
              },
              '& .MuiPickersInputBase-root.Mui-focused:not(:hover)': {
                backgroundColor: enabled,
              },
              '& .MuiPickersInputBase-root::after': {
                borderBottomColor: main,
              },
              '& .MuiPickersInputBase-root::before': {
                borderColor,
              },
              '& .MuiPickersInputBase-root.Mui-error::before, & .MuiPickersInputBase-root.Mui-error::after': {
                borderBottomColor: colors.error.main,
                borderColor: colors.error.main,
              },
              '& .MuiPickersInputBase-root.Mui-disabled': {
                backgroundColor: colors.contrast.enabled,
                color: colors.contrast.disabled,
              },
              '& .MuiPickersInputBase-root:not(.MuiPickersInputBase-inputSizeSmall)': {
                minHeight: '3.5rem',
              },
            }
          },
        },
      },
      MuiPickersToolbar: {
        styleOverrides: {
          toolbar: { backgroundColor: transparent },
        },
      },
      MuiPickersToolbarText: {
        styleOverrides: {
          toolbarTxt: {
            '&:not(h6)': { ...typography.h4, fontSize: 40 },
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          slotProps: { root: { sx: { '& .MuiBackdrop-root': { opacity: '0 !important' } } } },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.MuiRadio-colorPrimary': {
              color: main,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: 'filled',
        },
        styleOverrides: {
          disabled: {},
          icon: {
            color: main,
            right: '1rem',
            '.Mui-disabled &': {
              color: 'inherit',
            },
          },
          select: {
            '&:focus': {
              background: transparent,
            },
          },
          nativeInput: {
            '.MuiSelect-select + &': {
              pointerEvents: 'none',
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
            fontSize: spacing(1.75),
            paddingRight: spacing(6),
          },
          action: {
            position: 'absolute',
            right: spacing(2),
            top: 8,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: ({ ownerState }) => {
            if (ownerState.color === 'default' || ownerState.color === 'inherit') {
              return {
                color: 'inherit',
              }
            }
            return undefined
          },
          fontSizeDefault: {
            fontSize: '1.5rem',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: shallowEqualsMemoizer(({ ownerState, theme: { palette: colors, transitions } }) => {
            const { color: colorName = 'default' } = ownerState
            let { [colorName]: color } = colors
            if (!color) {
              const augmented = colors.augmentColor({ color: colors.grey })
              color = getStringableColorWithStates(augmented.main, 'main', states, augmented)
            }

            return {
              filter: `grayscale(${colors.states.inactive})`,
              transition: transitions.create(['filter'], {
                duration: transitions.duration.shortest,
                easing: transitions.easing.easeInOut,
              }),
              '&:has(.Mui-checked)': {
                filter: 'grayscale(0)',
              },
              '& .MuiButtonBase-root.MuiSwitch-switchBase': {
                color: color ? color.main : contrast.inactive,
                '& + .MuiSwitch-track': {
                  backgroundColor: color.disabled,
                },
              },
            }
          }, 4),
        },
      },
      MuiStepButton: {
        styleOverrides: {
          root: {
            '&.MuiButtonBase-root.Mui-disabled, &.MuiButtonBase-root:disabled': {
              opacity: states.disabled,
            },
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: states.disabled,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              background: inputColors.hovered,
              borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
              opacity: 1,
            },
            '&.Mui-disabled': {
              opacity: 0.32,
            },
            '&:not(.Mui-selected):not(.Mui-disabled)': {
              opacity: states.muted,
            },
          },
          textColorPrimary: {
            color: main,
          },
          textColorSecondary: {
            color: palette.secondary.main,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            '&.MuiTableCell-body, &.MuiTableCell-head': {
              borderBottomColor: palette.contrastInverse.enabled,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            background: palette.background.default,
            '&.Mui-TableRow-hover:hover': {
              backgroundColor: palette.background.secondary,
            },
          },
          hover: {},
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'filled',
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: palette.appPrimary.inactive,
            color: palette.appPrimary.strong,
            '&.Mui-selected': {
              backgroundColor: palette.appPrimary.active,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: main,
            fontSize: typography.body2.fontSize,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variant: 'body1',
        },
      },
    },
    cssVariables: true,
    darken,
    palette,
    pxToRem: options.pxToRem ?? pxToRem,
    shape: {
      borderRadius,
      ...options.shape,
    },
    spacing: options.spacing ?? SPACING,
    spacingValue: options.spacing ?? SPACING,
    typography: { ...typography, ...options?.typography },
  })

  return theme
})

export const buildThemeFromColor = shallowEqualsMemoizer(
  (initialPrimary = defaultPalette.primary, opts = EMPTY_OBJECT) => {
    let primary = initialPrimary
    if (opts.adjustColor && opts.bg) {
      const mainColor = getMainColor(primary.main, opts.bg)
      primary = { ...primary, main: mainColor.string() }
    }
    const contrastText = primary.contrastText ?? defaultPalette?.white?.strong ?? 'white'

    return buildTheme({
      type: 'dark',
      primary: {
        ...primary,
        ...getStringableColorWithStates(primary.main, 'main', states),
        contrastText,
      },
      secondary: opts.secondary ?? defaultPalette.primary,
      error: opts.error ?? defaultPalette?.error,
      background: opts.background ?? defaultPalette?.background,
      text: opts.text ?? defaultPalette?.text,
    })
  },
)
